export interface ShopKitModel {
  id: string;
  name: string;
  type: string;
  href: string;
  description: string;
  tag: string;
  price: string;
  features: string[];
  gallery: string[];
  thumbnail: string;
  originalPrice?: string;
  onSale?: boolean;
  hidden?: boolean;
  speciesPricing?: ShopSpeciesPricing[];
  sortOrder: number;
  featured?: boolean;
  status: 'active' | 'draft' | 'archived';
  createdAt: Date;
  updatedAt: Date;
  thumbnailUrl?: string;
  galleryUrls?: string[];
  modelUrl?: string;
}

export interface ShopSpeciesPricing {
  species: string;
  price: string;
  originalPrice?: string;
  default?: boolean;
}

export interface ShopHeroConfig {
  headline: string;
  headlineBold: string;
  subheadline: string;
  tagline: string;
  cta1Label: string;
  cta1Link: string;
  cta2Label: string;
  cta2Link: string;
  modelPath: string;
  showJoineryDetails: boolean;
  droneVideoUrl: string;
  droneVideoPoster: string;
  /** Video background for the How It Works section. Illustrates the timeline process. */
  howItWorksVideoUrl?: string;
}

export interface ShopSiteSettings {
  logoUrl: string;
  faviconUrl: string;
  contactEmail: string;
  contactPhone: string;
  contactLocation: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string[];
  footerBuilderLoginUrl: string;
  customDesignPrice: string;
  customDesignUrl: string;
  claimKitUrl: string;
  stripePublishableKey: string;
  /** Optional image URL for link previews (iMessage, social). Uses logoUrl if empty. */
  ogImageUrl?: string;
  /** ISO date string (YYYY-MM-DD) when the site-wide sale ends. Empty = no sale. */
  saleEndDate?: string;
  /** Optional time (HH:mm, 24h) when the sale ends on saleEndDate. Defaults to end of day. */
  saleEndTime?: string;
}

export interface ShopSiteData {
  hero: ShopHeroConfig;
  settings: ShopSiteSettings;
  updatedAt: Date;
  updatedBy: string;
}

export interface ShopTestimonial {
  id: string;
  name: string;
  location: string;
  quote: string;
  rating: number;
  photoUrl: string;
  kitId?: string;
  kitName?: string;
  sortOrder: number;
  status: 'active' | 'draft';
  createdAt: Date;
  updatedAt: Date;
}

export const DEFAULT_TESTIMONIAL: Omit<ShopTestimonial, 'id' | 'createdAt' | 'updatedAt'> = {
  name: '',
  location: '',
  quote: '',
  rating: 5,
  photoUrl: '',
  kitId: '',
  kitName: '',
  sortOrder: 0,
  status: 'draft',
};

export const DEFAULT_HERO: ShopHeroConfig = {
  headline: 'Timeless Craft,',
  headlineBold: 'Timber Frame Kits',
  subheadline: 'Each kit is precision-cut with traditional mortise-and-tenon joinery, ready for your builder to raise. Ships nationwide from Franklin, Tennessee.',
  tagline: 'Pre-Cut, Pre-Engineered Timber Frame Kits',
  cta1Label: 'Explore Kits',
  cta1Link: '#kits',
  cta2Label: 'Custom Design',
  cta2Link: '#custom',
  modelPath: '/models/bent.glb',
  showJoineryDetails: true,
  droneVideoUrl: '',
  droneVideoPoster: '',
  howItWorksVideoUrl: '',
};

export const DEFAULT_SETTINGS: ShopSiteSettings = {
  logoUrl: '',
  faviconUrl: '',
  contactEmail: 'info@shoptimberframekits.com',
  contactPhone: '(615) 900-8905',
  contactLocation: 'Franklin, Tennessee',
  seoTitle: 'Shop Timber Frame Kits | Pre-Cut, Pre-Engineered Timber Frame Kits',
  seoDescription: 'Pre-cut, pre-engineered timber frame kits with traditional mortise-and-tenon joinery. Barns, pavilions, pergolas, entryways, and cabins. Ships nationwide from Franklin, TN.',
  seoKeywords: [
    'timber frame kits',
    'timber frame barn kit',
    'timber frame pavilion kit',
    'post and beam kit',
    'mortise and tenon',
    'timber frame pergola',
    'timber frame cabin kit',
    'prefab timber frame',
  ],
  footerBuilderLoginUrl: 'https://shoptimberframekits.com/admin',
  customDesignPrice: '$500',
  customDesignUrl: 'https://shoptimberframekits.com/custom-design.html',
  claimKitUrl: 'https://shoptimberframekits.com/claim-kit.html',
  stripePublishableKey: '',
  saleEndDate: '',
  saleEndTime: '23:59',
};
